import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { apiRequest } from "@/lib/queryClient";
import { 
  Users, 
  Search,
  Mail,
  Phone,
  Building,
  UserCheck,
  Calendar
} from "lucide-react";
import { useState } from "react";

interface TendererAuth {
  email: string;
  accessCode: string;
  companyName: string;
  authenticatedAt: string;
}

interface CompanyMember {
  id: string;
  name?: string;
  email: string;
  jobTitle?: string;
  phone?: string;
  company: string;
  isActive: boolean;
  lastAccessed?: string;
  role?: string;
}

interface TendererMembersProps {
  authData: TendererAuth;
}

export function TendererMembers({ authData }: TendererMembersProps) {
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch company members
  const { data: members = [], isLoading } = useQuery<CompanyMember[]>({
    queryKey: ['/api/tenderer/company-members', authData?.email, authData?.accessCode],
    queryFn: () => apiRequest('/api/tenderer/company-members', {
      method: 'POST',
      body: JSON.stringify({
        email: authData?.email,
        accessCode: authData?.accessCode
      })
    }),
    enabled: !!authData?.email && !!authData?.accessCode,
  });

  const getInitials = (name: string) => {
    if (!name || typeof name !== 'string') {
      return '??';
    }
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter members based on search
  const filteredMembers = members.filter(member => 
    !searchTerm || 
    member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.jobTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.company.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group members by company
  const membersByCompany = filteredMembers.reduce((acc, member) => {
    if (!acc[member.company]) {
      acc[member.company] = [];
    }
    acc[member.company].push(member);
    return acc;
  }, {} as Record<string, CompanyMember[]>);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Members</h1>
        <p className="text-gray-600 mt-1">
          Company members working on projects with tender packages
        </p>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Company Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{members.length}</div>
            <p className="text-xs text-muted-foreground">
              Across all companies
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Members</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {members.filter(m => m.isActive).length}
            </div>
            <p className="text-xs text-muted-foreground">
              Currently active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Companies</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Object.keys(membersByCompany || {}).length}
            </div>
            <p className="text-xs text-muted-foreground">
              Total companies
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Members by Company */}
      <div className="space-y-6">
        {Object.entries(membersByCompany || {}).map(([companyName, companyMembers]) => (
          <Card key={companyName}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                {companyName}
                <Badge variant="secondary" className="ml-auto">
                  {companyMembers.length} member{companyMembers.length !== 1 ? 's' : ''}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {companyMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-blue-100 text-blue-700">
                        {getInitials(member.name)}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-gray-900 truncate">
                          {member.name || 'Unknown User'}
                        </h4>
                        {member.isActive ? (
                          <Badge className="bg-green-100 text-green-800" variant="secondary">
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-800" variant="secondary">
                            Inactive
                          </Badge>
                        )}
                      </div>
                      
                      {member.jobTitle && (
                        <p className="text-sm text-gray-600 truncate">{member.jobTitle}</p>
                      )}
                      
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Mail className="h-3 w-3" />
                          <span className="truncate">{member.email}</span>
                        </div>
                        
                        {member.phone && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Phone className="h-3 w-3" />
                            <span>{member.phone}</span>
                          </div>
                        )}
                        
                        {member.lastAccessed && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Calendar className="h-3 w-3" />
                            <span>Last active: {formatDate(member.lastAccessed)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
        
        {Object.keys(membersByCompany || {}).length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No members found</h3>
              <p className="text-gray-500">
                {searchTerm 
                  ? "Try adjusting your search criteria"
                  : "No company members found for your projects"
                }
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}